<script lang="ts" setup>
import { ref, computed } from 'vue';
import { useMutation } from '@vue/apollo-composable';
import { BucketUserAssociationDeleteDocument, UserType } from '@/gql/graphql';
import { BAlert, BButton, BModal, BSpinner } from 'bootstrap-vue-next';

// --- Component properties ---
const props = defineProps<{
  show: boolean;
  user: UserType;
  bucketId: number;
  bucketFriendlyName: string;
}>();

// --- Component emits ---
const emits = defineEmits(['close', 'closeAndRefresh']);

// --- Component state ---
const show = computed<boolean>(() => {
  return props.show;
});

// --- Mutation: Rename Data Object ---
const mutationInProgress = ref<boolean>(false);
const mutationError = ref<string | null>(null);
const mutationShowSuccess = ref<boolean>(false);

const {
  mutate: executeMutation,
  onDone: onMutationDone,
  onError: onMutationError,
} = useMutation(BucketUserAssociationDeleteDocument);

onMutationDone((res) => {
  mutationInProgress.value = false;
  if (res.data && res.data.bucketUserAssociationDelete) {
    console.debug(
      'Bucket-user association deleted:',
      res.data.bucketUserAssociationDelete
    );
    mutationShowSuccess.value = true;
    setTimeout(() => {
      mutationShowSuccess.value = false;
      emits('closeAndRefresh');
    }, 1000);
  } else {
    mutationError.value =
      'Bucket-user association deletion failed: ' + res.data;
    console.error('Bucket-user association deletion failed:', res.data);
  }
});
onMutationError((err) => {
  console.error('Error deleting bucket-user association:', err);
  mutationInProgress.value = false;
  mutationError.value = err.message;
});

// --- Handle component actions ---
function onDeletionConfirmed() {
  if (!props.user || !props.user.id) {
    mutationError.value = 'User ID not provided for deletion';
    console.error('User ID not provided for deletion');
    return;
  }
  mutationInProgress.value = true;
  mutationError.value = null;
  executeMutation({ bucketId: props.bucketId, userId: props.user.id });
}

function handleCloseModal() {
  emits('close');
}
</script>
<template>
  <div>
    <!-- MODAL -->
    <b-modal v-model="show" @hide="handleCloseModal">
      <!-- Title -->
      <template #title>
        <font-awesome-icon icon="trash" class="me-2" />
        Remove access to <strong>{{ props.bucketFriendlyName }}</strong> for
        <strong>
          {{ props.user.givenName }} {{ props.user.familyName }}
        </strong>
      </template>
      <!-- Content -->
      <div>
        <!-- Delete? Confirmation Message -->
        <p>
          Are you sure you want to remove access to data
          <strong> {{ props.bucketFriendlyName }} </strong> for
          <strong>
            {{ props.user.givenName }} {{ props.user.familyName }} ({{
              props.user.email
            }})
          </strong>
          ?
        </p>

        <!-- Error Message -->
        <b-alert
          v-model="mutationError"
          variant="danger"
          dismissible
          class="mt-3"
          @dismissed="mutationError = null"
        >
          {{ mutationError }}
        </b-alert>
      </div>
      <!-- Footer -->
      <template #footer>
        <b-button variant="secondary" @click="handleCloseModal">
          Cancel
        </b-button>
        <b-button
          variant="danger"
          :disabled="mutationInProgress || mutationShowSuccess"
          @click="onDeletionConfirmed"
        >
          <span v-if="mutationInProgress">
            Removing User Access
            <b-spinner small label=""></b-spinner>
          </span>
          <span v-else-if="mutationShowSuccess">
            User Access Removed
            <font-awesome-icon :icon="['fa', 'check-double']" class="me-2" />
          </span>
          <span v-else> Remove Access </span>
        </b-button>
      </template>
    </b-modal>
  </div>
</template>
